import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class TosService {
  private BASE_URL: string = environment.apiBaseURL;

  // 1. Modal Visibility State (Observed by AppComponent)
  private showModalSubject = new BehaviorSubject<boolean>(false);
  showModal$ = this.showModalSubject.asObservable();

  private resumeRequestsSubject = new Subject<void>();
  private oidcCancellation?: Subscription;
  acceptanceRevision = 0;
  recoveryRequired = false;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private router: Router,
  ) {
    this.auth.LogOutEvent.subscribe(() => this.resetSession());
  }

  waitForDecision(recoveryRequired: boolean): Observable<void> {
    return new Observable(subscriber => {
      // Attach lazily: a document can enter OIDC after starting on a legacy/public page.
      if (!this.oidcCancellation && this.auth.shouldUseOidc()) {
        this.oidcCancellation = this.auth.oidc.cancel$.subscribe(() => this.resetSession());
      }
      // Subscribe before opening the popup so an immediate decision cannot be missed.
      const subscription = this.resumeRequestsSubject.subscribe(subscriber);
      this.recoveryRequired = this.recoveryRequired || recoveryRequired;
      if (!this.showModalSubject.value) this.showModalSubject.next(true);
      return subscription;
    });
  }

  hideModal() {
    this.showModalSubject.next(false);
    this.recoveryRequired = false;
  }

  private resetSession() {
    this.acceptanceRevision = 0;
    this.hideModal();
  }

  retryRequests() {
    if (!this.showModalSubject.value || !this.recoveryRequired) return;

    this.hideModal();
    this.resumeRequestsSubject.next();
  }

  // Called by Modal "Agree" button
  acceptTos(): Observable<any> {
    const url = `${this.BASE_URL}/account/accept-tos`;

    return this.http.post(url, {}).pipe(
      tap(() => {
        this.acceptanceRevision++;
        this.hideModal();
        this.resumeRequestsSubject.next();
      })
    );
  }

  rejectTos() {
    const isOidcLogout = this.auth.logout();
    if (!isOidcLogout) {
      this.router.navigate(['login']);
    }
  }
}
